import { logger } from '@vagabondinternet/logging';
import { PROVIDER_PARAM } from '@words-happen/app-urls';
import { type SignInCredentials, SignInScreen } from '@words-happen/screens';
import { isEnabledProvider, type SocialProvider } from '@words-happen/ui-components';
import type React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import {
  AUTH_MESSAGES,
  authErrorCode,
  authFailure,
  browserIdentitySession,
  type IdentitySession,
  signInWithEmail as identityPlatformSignIn,
  identityPlatformSocialSignIn,
  type SignInWithEmail,
  type SocialSignIn,
  sharedAuthMessage,
} from '../../api';
import { ENABLED_AUTH_PROVIDERS } from '../../config/auth-providers';
import { type BrowserNavigation, routeState, useAuthNavigation } from '../../navigation';
import { ROUTES } from '../../routes';
import { HandoffScreen, useApiWarmUp, useSessionHandoff } from '../../session';
import { RESET_PASSWORD_MESSAGES } from '../reset-password/reset-password-messages';
import { useSocialSignIn } from '../use-social-sign-in';

/** Every credential failure, so sign-in never reveals whether an account exists. */
export const SIGN_IN_FAILED = "We couldn't sign you in. Check your email and password and try again.";

/**
 * The alert for a failed sign-in: too many attempts, network failures and the pre-launch allowlist's refusal have their
 * own, all else SIGN_IN_FAILED.
 */
export const signInErrorMessage = (error: unknown): string => {
  const failure = authFailure(error);
  return failure === 'not-allowed' ? AUTH_MESSAGES.signInNotOpen : (sharedAuthMessage(failure) ?? SIGN_IN_FAILED);
};

export interface SignInPageProps {
  signInWithEmail?: SignInWithEmail;
  /** Social sign-in with a redirect. Defaults to Identity Platform's, loading Firebase on first use. */
  socialSignIn?: SocialSignIn;
  /** The social providers that can be used; the others are shown "coming soon". Defaults to VITE_AUTH_PROVIDERS. */
  authProviders?: readonly SocialProvider[];
  /** How the back chevron and a signed-in player leave the app. Defaults to the window's history and location. */
  navigation?: BrowserNavigation;
  /** Firebase's signed-in user and the API's session endpoints. Defaults to the browser's. */
  session?: IdentitySession;
}

/**
 * /sign-in: email sign-in with Identity Platform for SignInScreen. A verified player's ID token is exchanged for a
 * words-happen session, then they go to the URL the API returned, or GAME_HOME_URL; an unverified one stays signed in
 * and goes to /verify-email. A player already signed in to Firebase is handed off without seeing the form, or sent to
 * /verify-email when unverified, as is one the API refuses as unverified (EMAIL_NOT_VERIFIED). An automatic redirect
 * from /verify-email is never sent straight back: the form shows instead. After a password reset whose automatic
 * sign-in failed, it says the password has changed and never signs in silently. "Forgot password?" goes to
 * /forgot-password with the address typed so far.
 *
 * Every failed credential sign-in (SIGN_IN_FAILED) also offers to create an account with the address typed, which goes
 * to /register with it in history state; the offer never depends on whether the account exists, which the page can't
 * tell anyway. Too many attempts, network failures and the allowlist's refusal have no offer.
 *
 * A social provider's button leaves for its sign-in page and comes back here, where the redirect is completed and the
 * player handed off like an email sign-in; silent sign-in doesn't run on that visit, so the handoff runs once. An
 * unverified player (Microsoft's addresses always arrive unverified) is sent the verification email and goes to
 * /verify-email.
 * Arriving with ?provider=<an enabled provider> (the landing screen's buttons) starts that provider's sign-in at once,
 * unless someone is already signed in on this device.
 *
 * On loading, the page warms the API up (session.warmUpApi), so a cold API starts while the player signs in.
 */
export const SignInPage: React.FC<SignInPageProps> = ({
  signInWithEmail = identityPlatformSignIn,
  socialSignIn = identityPlatformSocialSignIn,
  authProviders = ENABLED_AUTH_PROVIDERS,
  navigation,
  session = browserIdentitySession,
}) => {
  const { switchTo, back, leave, returnTo, continueUrl } = useAuthNavigation(navigation);
  const { state } = useLocation();
  const [params, setParams] = useSearchParams();
  const [arrival] = useState(() => routeState(state));
  // Prefills the form: the address handed over from another route, then the last one submitted here.
  const [email, setEmail] = useState(arrival.email);
  // Read when the handoff redirects, which may run from an earlier render's callbacks.
  const lastEmail = useRef(arrival.email);

  const toVerifyEmail = useCallback(
    (address: string | undefined, redirected: boolean, verificationSent?: boolean) =>
      switchTo(ROUTES.verifyEmail, {
        ...(address ? { email: address } : {}),
        ...(redirected ? { redirected } : {}),
        ...(verificationSent === undefined ? {} : { verificationSent }),
      }),
    [switchTo],
  );
  const redirectToVerifyEmail = useCallback(() => toVerifyEmail(lastEmail.current, true), [toVerifyEmail]);
  const social = useSocialSignIn({
    socialSignIn,
    enabled: authProviders,
    notAllowedMessage: AUTH_MESSAGES.signInNotOpen,
    continueUrl,
    // Runs after mount, once the handoff below exists.
    onSignedIn: (result) =>
      result.verified ? handoff.complete() : toVerifyEmail(undefined, false, result.verificationSent),
  });
  const handoff = useSessionHandoff({
    session,
    returnTo,
    leave,
    silent: !arrival.passwordChanged && !social.redirectPending,
    onUnverified: arrival.redirected ? undefined : redirectToVerifyEmail,
  });
  useApiWarmUp(session);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string>();

  // ?provider= from the landing screen: start that provider's sign-in, dropping the parameter first so coming back
  // from the provider's page never starts it again.
  const [autoProvider] = useState(() => params.get(PROVIDER_PARAM));
  const autoStarted = useRef(false);
  // biome-ignore lint/correctness/useExhaustiveDependencies: on mount only
  useEffect(() => {
    if (
      autoStarted.current ||
      !isEnabledProvider(authProviders, autoProvider) ||
      social.redirectPending ||
      arrival.passwordChanged ||
      session.mayBeSignedIn()
    ) {
      return;
    }
    autoStarted.current = true;
    setParams(
      (current) => {
        current.delete(PROVIDER_PARAM);
        return current;
      },
      { replace: true, state },
    );
    social.select(autoProvider);
  }, []);

  const submit = async (credentials: SignInCredentials) => {
    setEmail(credentials.email);
    lastEmail.current = credentials.email;
    setError(undefined);
    social.clearError();
    setSubmitting(true);
    try {
      const result = await signInWithEmail(credentials);
      if (result.verified) {
        await handoff.complete();
        setSubmitting(false);
        return;
      }
      toVerifyEmail(credentials.email, false);
    } catch (cause) {
      logger.error({ message: 'Email sign-in failed', code: authErrorCode(cause) });
      setError(signInErrorMessage(cause));
    }
    setSubmitting(false);
  };

  if (handoff.state.phase !== 'idle' || social.finishing) {
    return <HandoffScreen handoff={handoff} />;
  }

  return (
    <SignInScreen
      submitting={submitting}
      error={error ?? social.error}
      defaultEmail={email}
      notice={arrival.passwordChanged ? RESET_PASSWORD_MESSAGES.passwordChanged : undefined}
      onSubmit={submit}
      enabledProviders={authProviders}
      busyProvider={social.busyProvider}
      onSelectProvider={social.select}
      onForgotPassword={(typed) => switchTo(ROUTES.forgotPassword, typed ? { email: typed } : undefined)}
      registerOffer={error === SIGN_IN_FAILED}
      onRegister={(typed) => switchTo(ROUTES.register, typed ? { email: typed } : undefined)}
      onBack={back}
    />
  );
};
